import classes from './reset-password.module.scss';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Wrapper } from '../../components/shared/wrapper.component';
import { RiLockPasswordFill, RiLockPasswordLine } from 'react-icons/ri';
import useForgotPassword from '../../Hooks/forgot-password.hook';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'antd/es/form/Form';
import { Constants } from '../../constants/constants';
import useDocumentTitle from '../../Hooks/document-title.hook';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const { t } = useTranslation();
  const { resetPasswordByToken, state } = useForgotPassword();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const userId = searchParams.get('userId') ?? '';
  const [form] = useForm();
  useDocumentTitle('Reset Password')

  const resetPassword = async () => {
    await resetPasswordByToken({
      token: decodeURIComponent(token),
      userId: decodeURIComponent(userId),
      password: form.getFieldValue('password'),
    });
  };

  return (
    <Wrapper>
      <div className={classes.resetPasswordContainer}>
        <div className={classes.textContainer}>
          <h1 className={classes.title}>{t('reset-password.title')}</h1>
          <p className={classes.text}>
            {t('reset-password.description')}
          </p>
        </div>
        <Form
          size="large"
          form={form}
          layout="vertical"
          name="reset-password"
          onFinish={resetPassword}
          className={classes.resetPasswordForm}
          scrollToFirstError
          disabled={state.loading}
          requiredMark={false}
        >
          <Form.Item
            className={classes.formItem}
            name="password"
            rules={[
              {
                required: true,
                message: t('common.required'),
              },
              {
                pattern: Constants.PASSWORD_PATTERN,
                message: t('common.password-rules'),
              },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<RiLockPasswordLine />}
              placeholder={t('common.password')}
              size="large"
              className={classes.inputField}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            className={classes.formItem}
            name="confirm"
            dependencies={['password']}
            hasFeedback
            rules={[
              {
                required: true,
                message: t('common.required'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error(t('common.password-mismatch')),
                  );
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<RiLockPasswordFill />}
              placeholder={t('common.confirm-password')}
              size="large"
              className={classes.inputField}
            />
          </Form.Item>
          <Form.Item>
            <Button
              className={classes.resetPasswordBtn}
              disabled={state.loading}
              type="primary"
              htmlType="submit"
            >
              {t('reset-password.update')}
            </Button>
            {state.loading && (
              <Spin
                className={classes.feedback}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            )}
          </Form.Item>
        </Form>
        {state.type === 'error' && (
          <Alert
            className={classes.feedback}
            showIcon
            message={
              Array.isArray(state.result?.message)
                ? state.result?.message.map((msg, id) => {
                  return <p key={id}>{msg}</p>;
                })
                : state.result?.message
            }
            type="error"
            closable
          />
        )}
      </div>
    </Wrapper>
  );
};

export default ResetPassword;
